import {BaseComponent, TextAlign} from "@renta-apps/athenaeum-react-common";
import {CellAction, CellModel, ColumnActionType, ColumnDefinition, ColumnType, Grid, GridHoveringType, GridOddType} from "@renta-apps/athenaeum-react-components";
import React from "react";
import {ActionType, AttachmentType} from "@/models/Enums";
import WorkReportAttachment from "@/models/server/WorkReportAttachment";
import {IPagedList, SortDirection} from "@renta-apps/athenaeum-toolkit";
import Localizer from "@/localization/Localizer";
import RentaTaskConstants from "@/helpers/RentaTaskConstants";
import FileTypeTranslator from "@/helpers/FileTypeTranslator";
import FeatureFlags from "@/helpers/FeatureFlags";
import UnleashHelper from "@/helpers/UnleashHelper";
import UpdateAttachmentCommentRequest from "@/models/server/requests/UpdateAttachmentCommentRequest";


interface IWorkOrderAttachmentsGridProps {
    readonly?: boolean;
    displayPublicity?: boolean;
    attachmentsSortColumn?: string;
    attachmentsSortDirection?: SortDirection;

    fetchAttachments(pageNumber: number,
                     pageSize: number,
                     sortColumnName: string | null,
                     sortDirection: SortDirection | null): Promise<IPagedList<WorkReportAttachment>>
    processAttachmentOperation?(cell: CellModel<WorkReportAttachment>, action: CellAction<WorkReportAttachment>): Promise<void>;
}

export class WorkOrderAttachmentsGrid extends BaseComponent<IWorkOrderAttachmentsGridProps> {

    // Fields

    private readonly _gridRef: React.RefObject<Grid<WorkReportAttachment>> = React.createRef();

    private readonly _attachmentsColumns: ColumnDefinition[] = [
        {
            header: "#",
            accessor: "#",
            textAlign: TextAlign.Left,
            minWidth: 40,
            stretch: false
        },
        {
            header: Localizer.workOrderDetailsPanelGridAttachmentsFileNameLanguageItemName,
            accessor: nameof.full<WorkReportAttachment>(a => a.file.name),
            textAlign: TextAlign.Left,
            minWidth: 230,
            sorting: true,
            init: (cell: CellModel<WorkReportAttachment>) => this.initFileNameColumn(cell),
            settings: {
                descriptionAccessor: nameof<WorkReportAttachment>(a => a.comment),
                descriptionTitle: Localizer.genericCommentLanguageItemName,
                descriptionMaxLength: 1024,
                descriptionCallback: (cell: CellModel<WorkReportAttachment>) => this.onCommentChangeAsync(cell)
            },
        },
        {
            header: Localizer.workOrderDetailsPanelGridAttachmentsFileTypeLanguageItemName,
            accessor: nameof.full<WorkReportAttachment>(a => a.file.type),
            textAlign: TextAlign.Left,
            sorting: true,
            transform: (_, cellValue: string, __) => FileTypeTranslator.translate(cellValue),
        },
        {
            header: Localizer.workOrderDetailsPanelGridAttachmentsAttachmentTypeLanguageItemName,
            accessor: "type",
            format: nameof<AttachmentType>(),
            textAlign: TextAlign.Left,
            sorting: true
        },
        {
            header: Localizer.workOrderDetailsPanelGridAttachmentsPublicLanguageItemName,
            accessor: nameof<WorkReportAttachment>(a => a.public),
            type: ColumnType.Boolean,
            textAlign: TextAlign.Left,
            visible: this.displayPublicity,
            sorting: true
        },
        {
            header: Localizer.workOrderDetailsPanelGridAttachmentsRequiresAcknowledgment,
            accessor: nameof<WorkReportAttachment>(a => a.requiresAcknowledgment),
            type: ColumnType.Boolean,
            textAlign: TextAlign.Left,
            visible: !this.readonly && UnleashHelper.isEnabled(FeatureFlags.AttachmentAcknowledgment),
            sorting: true,
        },
        {
            header: Localizer.tasksPanelActionsLanguageItemName,
            minWidth: 180,
            init: (cell) => this.initAttachmentOperations(cell),
            actions: [
                {
                    name: "save",
                    title: Localizer.genericActionSaveLanguageItemName,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell, action) => await this.processAttachmentOperationAsync(cell, action)
                },
                {
                    name: "cancel",
                    title: Localizer.genericActionCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    callback: async (cell, action) => await this.processAttachmentOperationAsync(cell, action)
                },
                {
                    name: "delete",
                    title: Localizer.tasksPanelDeleteAttachmentLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    confirm: (cell: CellModel<WorkReportAttachment>) => cell.model.id && Localizer.workOrderDetailsPanelGridAttachmentsConfirmDelete.format(cell.model.file.name),
                    callback: async (cell, action) => await this.processAttachmentOperationAsync(cell, action)
                },
                {
                    name: "preview",
                    title: Localizer.genericActionPreviewLanguageItemName,
                    type: ColumnActionType.Preview,
                    right: true,
                    callback: async (cell, action) => await this.processAttachmentOperationAsync(cell, action)
                },
                {
                    title: Localizer.tasksPanelDownloadWorkReportLanguageItemName,
                    type: ColumnActionType.Download,
                    right: true,
                    callback: async (cell, action) => await this.processAttachmentOperationAsync(cell, action)
                }
            ]
        },
    ];

    // Properties

    private get displayPublicity(): boolean {
        return (this.props.displayPublicity === true);
    }

    private get readonly(): boolean {
        return (this.props.readonly === true);
    }

    public get gridRef(): Grid<WorkReportAttachment> | null {
        return this._gridRef.current;
    }


    // Methods

    private initAttachmentOperations(cell: CellModel<WorkReportAttachment>): void {
        const attachment: WorkReportAttachment = cell.row.model;

        const readonly: boolean = (this.readonly);
        const modified: boolean = (cell.row.modified);
        const isValid: boolean = (attachment.file.isFileModel);
        const isNew: boolean = (!attachment.id);
        const canPreview: boolean = (RentaTaskConstants.previewFileTypes.includes(attachment.file.type));

        const saveAction: CellAction<WorkReportAttachment> = cell.actions[0];
        const cancelAction: CellAction<WorkReportAttachment> = cell.actions[1];
        const deleteAction: CellAction<WorkReportAttachment> = cell.actions[2];
        const previewAction: CellAction<WorkReportAttachment> = cell.actions[3];
        const downloadAction: CellAction<WorkReportAttachment> = cell.actions[4];

        saveAction.visible = (!readonly) && (modified) && (isValid);
        cancelAction.visible = (!readonly) && (modified) && (!isNew);
        deleteAction.visible = (!readonly) && (isValid);
        previewAction.visible = (isValid) && (canPreview);
        downloadAction.visible = (isValid) && (!isNew);
    }

    private async processAttachmentOperationAsync(cell: CellModel<WorkReportAttachment>, action: CellAction<WorkReportAttachment>): Promise<void> {
        await this.props.processAttachmentOperation?.(cell, action);
    }

    private initFileNameColumn(cell: CellModel<WorkReportAttachment>): void {
        const isAttachmentCommentEnabled = UnleashHelper.isEnabled(FeatureFlags.AttachmentAcknowledgment);
    
        cell.descriptionReadonly = this.readonly;

        // Hide description action if not enabled
        if (cell.descriptionAction && (!isAttachmentCommentEnabled)) {
            cell.descriptionAction.visible = false;
        }
    }

    private async onCommentChangeAsync(cell: CellModel<WorkReportAttachment>): Promise<void> {
        const model: WorkReportAttachment = cell.row.model;
        const request = new UpdateAttachmentCommentRequest();
        request.id = model.id;
        request.comment = model.comment;

        await cell.grid.postAsync("api/workOrders/updateAttachmentComment", request);

        // Re-render after comment was edited
        await cell.reRenderAsync();
    }

    private async fetchAttachments(pageNumber: number,
                                   pageSize: number,
                                   sortColumnName: string | null,
                                   sortDirection: SortDirection | null): Promise<IPagedList<WorkReportAttachment>> {
        return await this.props.fetchAttachments(pageNumber, pageSize, sortColumnName, sortDirection);
    }

    public async reloadAsync(): Promise<void> {
        await this._gridRef.current?.reloadAsync();
    }

    public render() {
        return (
            <Grid ref={this._gridRef}
                  columns={this._attachmentsColumns}
                  readonly={this.readonly}
                  minWidth="auto"
                  className={"mr-auto"}
                  noDataText={Localizer.workOrderDetailsPanelGridAttachmentsNoDataText}
                  hovering={GridHoveringType.Row}
                  odd={GridOddType.None}
                  fetchData={async (_, pageNumber, pageSize, sortColumnName, sortDirection) => await this.fetchAttachments(pageNumber, pageSize, sortColumnName, sortDirection)}
                  pagination={RentaTaskConstants.paginationNumber}
                  defaultSortColumn={this.props.attachmentsSortColumn}
                  defaultSortDirection={this.props.attachmentsSortDirection}
            />
        );
    }
}